import { useCallback, useRef, useTransition } from "react";
import { useRecoilValue } from "recoil";

import {
  useLiveMapIncidentsActions,
  usePageContext,
  useRidesActions,
  useTrackingActions,
} from "@ryde/redux";
import {
  computeLiveMapDateRangeFromHours,
  createErrorResult,
  createSuccessResult,
  DatesUnit,
  DEFAULT_LIVE_MAP_HOURS_BACK,
  DEFAULT_LIVE_MAP_HOURS_FORWARD,
  errors,
  IRideTableDto,
  isLiveMapReducedDisplayMode,
  ITrackingBatchRideTrackingsDto,
  liveMapEffectiveDisplayModeSelector,
  liveMapHoursBackOverrideAtom,
  liveMapHoursForwardOverrideAtom,
  liveMapPlannedForNowAtomFamily,
  liveMapRideFilterAtomFamily,
  resolveLiveMapHours,
  RideFetchDataMode,
  SimpleResult,
  useDebugMode,
  useHasPermissions,
  useSystemParams,
  useTimezone,
} from "@ryde/shared";

import {
  abortPollInFlight,
  beginPollAbort,
  endPollAbort,
} from "./use-live-map-fetch.utils";

export const useLiveMapFetch = () => {
  const { entity } = usePageContext();
  const { fetchRides } = useRidesActions();
  const { fetchRideTrackings, fetchRideTrackingsBatch } = useTrackingActions();
  const { fetchLiveMapIncidentsForRideIds } = useLiveMapIncidentsActions();
  const { debugMode } = useDebugMode();
  const { timezone } = useTimezone();
  const [, startTransition] = useTransition();
  const gridPollAbortRef = useRef<AbortController | null>(null);
  const trackingsPollAbortRef = useRef<AbortController | null>(null);
  const incidentsPollAbortRef = useRef<AbortController | null>(null);
  const { hasPermissions: hasPermissionFetchTracking } = useHasPermissions({
    entity: "tracking",
    action: "read",
  });
  const { hasPermissions: hasIncidentRead } = useHasPermissions({
    entity: "incident",
    action: "read",
  });
  const { enableIncidents, liveMapHoursBack, liveMapHoursForward } =
    useSystemParams();
  const hoursBackOverride = useRecoilValue(liveMapHoursBackOverrideAtom);
  const hoursForwardOverride = useRecoilValue(liveMapHoursForwardOverrideAtom);
  const liveMapDisplayMode = useRecoilValue(
    liveMapEffectiveDisplayModeSelector,
  );
  const lastTrackingOnly = isLiveMapReducedDisplayMode(liveMapDisplayMode);
  const liveMapPlannedForNow = useRecoilValue(
    liveMapPlannedForNowAtomFamily(entity),
  );
  const liveMapRideFilter = useRecoilValue(liveMapRideFilterAtomFamily(entity));
  const isIncidentsEnabled = Boolean(enableIncidents);
  const canPollLiveMapIncidents = hasIncidentRead && isIncidentsEnabled;

  const computeLiveMapDateRange = useCallback((): DatesUnit | undefined => {
    if (!timezone) {
      return undefined;
    }
    const effectiveHoursBack = resolveLiveMapHours({
      overrideValue: hoursBackOverride,
      systemParamValue: liveMapHoursBack,
      fallback: DEFAULT_LIVE_MAP_HOURS_BACK,
    });
    const effectiveHoursForward = resolveLiveMapHours({
      overrideValue: hoursForwardOverride,
      systemParamValue: liveMapHoursForward,
      fallback: DEFAULT_LIVE_MAP_HOURS_FORWARD,
    });
    return computeLiveMapDateRangeFromHours({
      timezone,
      hoursBack: effectiveHoursBack,
      hoursForward: effectiveHoursForward,
    });
  }, [
    timezone,
    hoursBackOverride,
    hoursForwardOverride,
    liveMapHoursBack,
    liveMapHoursForward,
  ]);
  const abortGridPollInFlight = useCallback(() => {
    abortPollInFlight(gridPollAbortRef);
  }, []);
  const abortMapPollInFlight = useCallback(() => {
    abortPollInFlight(trackingsPollAbortRef);
    abortPollInFlight(incidentsPollAbortRef);
  }, []);
  const runInTransition = useCallback(
    <T>(fn: () => Promise<T>): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        startTransition(() => {
          void fn().then(resolve, reject);
        });
      }),
    [startTransition],
  );
  const backgroundFetchRides = useCallback((): Promise<
    SimpleResult<IRideTableDto[]>
  > => {
    const controller = beginPollAbort(gridPollAbortRef);
    const datesRangeOverride = computeLiveMapDateRange();
    return runInTransition(() =>
      fetchRides({
        backgroundAction: true,
        dataMode: RideFetchDataMode.LiveMap,
        debugMode,
        abortController: controller,
        datesRangeOverride,
        liveMapPlannedForNow,
        liveMapRideFilter,
      }).finally(() => endPollAbort(gridPollAbortRef, controller)),
    );
  }, [
    runInTransition,
    fetchRides,
    debugMode,
    computeLiveMapDateRange,
    liveMapPlannedForNow,
    liveMapRideFilter,
  ]);
  const backgroundFetchTrackings = useCallback(
    (rideId: number) => {
      if (!hasPermissionFetchTracking) {
        return;
      }
      const controller = beginPollAbort(trackingsPollAbortRef);
      void runInTransition(() =>
        fetchRideTrackings(rideId, {
          backgroundAction: true,
          debugMode,
          abortController: controller,
          lastTrackingOnly,
        }).finally(() => endPollAbort(trackingsPollAbortRef, controller)),
      );
    },
    [
      hasPermissionFetchTracking,
      fetchRideTrackings,
      debugMode,
      lastTrackingOnly,
      runInTransition,
    ],
  );
  const backgroundFetchTrackingsBatch = useCallback(
    (rideIds: readonly number[]): Promise<void> => {
      if (!hasPermissionFetchTracking) {
        return Promise.resolve();
      }
      const ids = [...new Set(rideIds)].filter(id => id > 0);
      if (!ids.length) {
        return Promise.resolve();
      }
      const controller = beginPollAbort(trackingsPollAbortRef);
      return runInTransition(() =>
        fetchRideTrackingsBatch(ids, {
          backgroundAction: true,
          debugMode,
          abortController: controller,
          lastTrackingOnly,
        })
          .then(() => undefined)
          .finally(() => endPollAbort(trackingsPollAbortRef, controller)),
      );
    },
    [
      hasPermissionFetchTracking,
      fetchRideTrackingsBatch,
      debugMode,
      lastTrackingOnly,
      runInTransition,
    ],
  );
  const fetchRidesInitial = useCallback(async () => {
    const controller = beginPollAbort(gridPollAbortRef);
    const datesRangeOverride = computeLiveMapDateRange();
    try {
      return await fetchRides({
        debugMode,
        dataMode: RideFetchDataMode.LiveMap,
        abortController: controller,
        datesRangeOverride,
        liveMapPlannedForNow,
        liveMapRideFilter,
      });
    } finally {
      endPollAbort(gridPollAbortRef, controller);
    }
  }, [
    fetchRides,
    debugMode,
    computeLiveMapDateRange,
    liveMapPlannedForNow,
    liveMapRideFilter,
  ]);
  const fetchTrackingsInitial = useCallback(
    async (rideId: number) => {
      if (!hasPermissionFetchTracking) {
        return;
      }
      const controller = beginPollAbort(trackingsPollAbortRef);
      try {
        return await fetchRideTrackings(rideId, {
          backgroundAction: true,
          debugMode,
          abortController: controller,
          lastTrackingOnly,
        });
      } finally {
        endPollAbort(trackingsPollAbortRef, controller);
      }
    },
    [
      hasPermissionFetchTracking,
      fetchRideTrackings,
      debugMode,
      lastTrackingOnly,
    ],
  );
  const backgroundFetchLiveMapIncidentsBatch = useCallback(
    (rideIds: readonly number[]): Promise<void> => {
      if (!canPollLiveMapIncidents) {
        return Promise.resolve();
      }
      const ids = [...new Set(rideIds)].filter(id => id > 0);
      if (!ids.length) {
        return Promise.resolve();
      }
      const controller = beginPollAbort(incidentsPollAbortRef);
      return runInTransition(() =>
        fetchLiveMapIncidentsForRideIds(ids, {
          backgroundAction: true,
          abortController: controller,
        })
          .then(() => undefined)
          .finally(() => endPollAbort(incidentsPollAbortRef, controller)),
      );
    },
    [canPollLiveMapIncidents, fetchLiveMapIncidentsForRideIds, runInTransition],
  );
  const fetchLiveMapIncidentsInitialBatch = useCallback(
    async (rideIds: readonly number[]) => {
      if (!canPollLiveMapIncidents) {
        return true;
      }
      const ids = [...new Set(rideIds)].filter(id => id > 0);
      if (!ids.length) {
        return true;
      }
      const controller = beginPollAbort(incidentsPollAbortRef);
      try {
        return await fetchLiveMapIncidentsForRideIds(ids, {
          backgroundAction: true,
          abortController: controller,
        });
      } finally {
        endPollAbort(incidentsPollAbortRef, controller);
      }
    },
    [fetchLiveMapIncidentsForRideIds, canPollLiveMapIncidents],
  );
  const fetchTrackingsInitialBatch = useCallback(
    async (
      rideIds: readonly number[],
    ): Promise<SimpleResult<ITrackingBatchRideTrackingsDto[]>> => {
      if (!hasPermissionFetchTracking) {
        return createErrorResult(
          errors.common.forbiddenResponseByPermission,
          "No permission to fetch trackings",
        );
      }
      const ids = [...new Set(rideIds)].filter(id => id > 0);
      if (!ids.length) {
        return createSuccessResult([]);
      }
      const controller = beginPollAbort(trackingsPollAbortRef);
      try {
        return await fetchRideTrackingsBatch(ids, {
          backgroundAction: true,
          debugMode,
          abortController: controller,
          lastTrackingOnly,
        });
      } finally {
        endPollAbort(trackingsPollAbortRef, controller);
      }
    },
    [
      hasPermissionFetchTracking,
      fetchRideTrackingsBatch,
      debugMode,
      lastTrackingOnly,
    ],
  );
  return {
    abortGridPollInFlight,
    abortMapPollInFlight,
    backgroundFetchRides,
    backgroundFetchTrackings,
    backgroundFetchTrackingsBatch,
    backgroundFetchLiveMapIncidentsBatch,
    fetchRidesInitial,
    fetchTrackingsInitial,
    fetchTrackingsInitialBatch,
    fetchLiveMapIncidentsInitialBatch,
  };
};
